import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowClockwise, ArrowSquareOut, Info, Lightning, SealCheck, Warning } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { adminApi, getErrorMessage } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Pill, Stat, aud, btnGhost } from "../ui";
import { AgreementPill, ConfidenceRing, DecisionPill, ScoreBars, fmtDate, outcomeLabel } from "./parts";
import { PricingTable } from "./PricingTable";

const H = ({ children }) => <div className="text-[0.62rem] uppercase tracking-[0.16em] font-bold text-neutral-500 mb-2">{children}</div>;

function List({ title, items, testId }) {
  if (!items?.length) return null;
  return <div data-testid={testId}><H>{title}</H><ul className="list-disc pl-5 text-xs text-neutral-300 space-y-1">{items.map((x, i) => <li key={i}>{x}</li>)}</ul></div>;
}

function Blockers({ a }) {
  return (
    <div className="border border-neutral-800 rounded-xl p-4" data-testid="wi-assess-blockers">
      <div className="flex items-center gap-2 text-sm mb-2">
        {a.auto_eligible ? <><Lightning size={16} weight="fill" className="text-[#73bf44]" /><span className="text-[#73bf44] font-semibold">Would qualify for auto-approval</span></>
          : <><Warning size={16} className="text-amber-400" /><span className="text-neutral-200 font-semibold">Needs a person to sign off</span></>}
      </div>
      {a.blockers.length > 0 && (
        <ul className="space-y-1 text-xs">{a.blockers.map((b) => <li key={b.key} className="text-amber-300/90">{b.label}{b.detail ? <span className="text-neutral-500"> — {b.detail}</span> : ""}</li>)}</ul>
      )}
    </div>
  );
}

function Human({ a }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Claimed" value={aud(a.totals?.claimed_inc_gst, 2)} sub="inc GST" tone="gold" />
      <Stat label="Lines total" value={aud(a.totals?.lines_ex_gst, 2)} sub="ex GST" />
      <Stat label="Human decision" value={<span className="text-base">{outcomeLabel(a.human?.outcome)}</span>} sub={a.human?.status || "—"} testId="wi-assess-human" />
      <Stat label="Approved amount" value={aud(a.human?.amount, 2)} sub={a.human?.basis ? `from ${a.human.basis}` : "—"} />
    </div>
  );
}

function Body({ a, onRerun, busy, paused }) {
  const ai = a.ai || {};
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <ConfidenceRing value={a.confidence} size={76} testId="wi-assess-confidence" />
        <div className="space-y-1.5">
          <DecisionPill decision={a.decision} testId="wi-assess-decision" />
          <div className="flex gap-1.5 flex-wrap"><AgreementPill value={a.agreement} testId="wi-assess-agreement" /><Pill>{a.mode === "shadow" ? "Shadow mode" : "Past-claims test"}</Pill></div>
          <p className="text-[0.7rem] text-neutral-500">Assessed {fmtDate(a.assessed_at)} · {a.model}</p>
        </div>
      </div>
      <div className="text-xs text-sky-200/90 bg-sky-500/[0.07] border border-sky-500/25 rounded-lg px-3 py-2 flex gap-2" data-testid="wi-assess-shadow-note">
        <Info size={15} className="shrink-0 mt-px" /> Suggestion only. Nothing was approved, emailed or changed in Monday.com.
      </div>
      {ai.summary && <p className="text-sm text-neutral-200 leading-relaxed" data-testid="wi-assess-summary">{ai.summary}</p>}
      <Human a={a} />
      <Blockers a={a} />
      <div><H>Score breakdown</H><ScoreBars scores={a.scores} weights={a.weights} /></div>
      <div className="grid md:grid-cols-2 gap-4 text-xs">
        <div><H>Repair scope</H><p className="text-neutral-300">{ai.scope_notes || "—"}</p></div>
        <div><H>Warranty eligibility · {(ai.warranty_category || "unclear").replace(/_/g, " ")}</H><p className="text-neutral-300">{ai.eligibility_notes || "—"}</p></div>
      </div>
      <div><H>Pricing vs historical approvals{a.totals?.pricing_note ? ` · ${a.totals.pricing_note}` : ""}</H><PricingTable lines={a.lines} concerns={ai.line_concerns} /></div>
      <div className="grid md:grid-cols-2 gap-4">
        <List title="Missing information" items={ai.missing_information} testId="wi-assess-missing" />
        <List title="Questions for the dealer" items={ai.questions_for_dealer} testId="wi-assess-questions" />
      </div>
      {a.history?.length > 0 && <List title="Earlier claims on this caravan" items={a.history.map((h) => `${h.date} · ${h.otma || "—"} · ${outcomeLabel(h.outcome)} — ${h.issue}`)} testId="wi-assess-history" />}
      <div className="flex flex-wrap gap-2 pt-1">
        <button className={btnGhost} onClick={onRerun} disabled={busy || paused} title={paused ? "Emergency pause is on" : undefined} data-testid="wi-assess-rerun-btn"><ArrowClockwise size={14} /> Re-assess</button>
        {a.monday_url && <a href={a.monday_url} target="_blank" rel="noreferrer" className={btnGhost} data-testid="wi-assess-monday-link"><ArrowSquareOut size={14} /> Open in Monday.com</a>}
        {a.mode === "shadow" && ["pending", "info"].includes(a.human?.outcome) && (
          <Link to={`/admin/warranty-intel/approvals?claim=${a.claim_id}`} className={btnGhost} data-testid="wi-assess-approve-link"><SealCheck size={14} /> Review & approve</Link>
        )}
      </div>
    </div>
  );
}

export function AssessmentDrawer({ row, paused, onClose, onChanged }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const wasRunning = useRef(false);
  const load = useCallback(() => adminApi.get(`/admin/wi/assessments/${row.claim_id}`, { params: { mode: row.mode } }).then(({ data }) => setA(data)).catch(() => {}), [row]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 4000, a?.status === "running");
  useEffect(() => {
    if (a?.status === "running") wasRunning.current = true;
    else if (a && wasRunning.current) { wasRunning.current = false; onChanged?.(); }
  }, [a, onChanged]);
  const rerun = async () => {
    setBusy(true);
    try { await adminApi.post(`/admin/wi/assessments/${row.claim_id}/run`, null, { params: { mode: row.mode } }); toast.success("Re-assessing — this takes about 30 seconds"); setA({ ...a, status: "running" }); onChanged?.(); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-neutral-800 text-neutral-200 w-full sm:max-w-3xl overflow-y-auto" data-testid="wi-assess-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-heading text-white text-left">{row.otma || "No OTMA number"} <span className="font-mono text-sm text-neutral-400">{row.chassis}</span></SheetTitle>
          <SheetDescription className="text-left text-neutral-500">{row.model} · {row.dealer} · {row.claim_date}</SheetDescription>
        </SheetHeader>
        {!a ? <div className="h-40 animate-pulse" /> : a.status === "running" ? (
          <div className="py-16 text-center text-sm text-neutral-400" data-testid="wi-assess-running"><ArrowClockwise size={22} className="mx-auto mb-2 animate-spin text-[#73bf44]" />AI is assessing this claim…</div>
        ) : a.status === "failed" ? (
          <div className="space-y-3"><p className="text-sm text-red-400">Assessment failed: {a.error}</p><button className={btnGhost} onClick={rerun} disabled={busy || paused}>Try again</button></div>
        ) : <Body a={a} onRerun={rerun} busy={busy} paused={paused} />}
      </SheetContent>
    </Sheet>
  );
}
